import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import BackToTopButton from '../../components/feature/BackToTopButton';

const ServiciosPage = () => {
  useEffect(() => {
    document.title = 'Servicios de Pladur en Mataró | Tabiques, Techos y Aislamientos - PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Servicios de instalación de pladur en Mataró: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico. Presupuesto personalizado ☎ 629 501 941'
      );
    }

    // Add WebPage Schema.org
    const existingWebPageSchema = document.querySelector('script[data-schema="webpage"]');
    if (existingWebPageSchema) existingWebPageSchema.remove();
    
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/servicios#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/servicios`,
      "name": "Servicios de Pladur en Mataró | Tabiques, Techos y Aislamientos - PLADUREX",
      "description": "Servicios de instalación de pladur en Mataró: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico. Presupuesto personalizado ☎ 629 501 941",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      }
    };
    
    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.setAttribute('data-schema', 'webpage');
    webPageScript.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(webPageScript);

    // Add BreadcrumbList Schema.org
    const existingSchema = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingSchema) existingSchema.remove();
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": import.meta.env.VITE_SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Servicios",
          "item": `${import.meta.env.VITE_SITE_URL}/servicios`
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'breadcrumb');
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (schemaScript) schemaScript.remove();
      const webPageSchemaScript = document.querySelector('script[data-schema="webpage"]');
      if (webPageSchemaScript) webPageSchemaScript.remove();
    };
  }, []);

  const services = [
    {
      id: 'tabiques',
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      description: 'Divisiones interiores rápidas y limpias para redistribuir espacios',
      details:
        'Los tabiques de pladur son la solución perfecta para crear nuevas habitaciones, dividir espacios abiertos o separar zonas en viviendas, oficinas y locales. Se montan sobre estructura metálica autoportante, permitiendo pasar instalaciones eléctricas y de fontanería por su interior.',
      includes: [
        'Estructura metálica autoportante',
        'Placas de pladur estándar o antihumedad',
        'Aislamiento acústico opcional (lana de roca)',
        'Tratamiento de juntas y acabado liso',
        'Preparación para pintar'
      ],
      useCases: [
        'Crear dormitorios adicionales',
        'Separar zonas de trabajo en oficinas',
        'Dividir locales comerciales',
        'Vestidores y zonas de almacenaje'
      ],
      priceFactors:
        'El precio depende de los m² de tabique, la altura del techo, si lleva aislamiento acústico, el tipo de placa (estándar o antihumedad) y el nivel de acabado.',
      image:
        'https://public.readdy.ai/ai/img_res/72868dc1e21af64356364b07bd64760f.jpg'
    },
    {
      id: 'trasdosados',
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Directos y autoportantes con opciones de aislamiento',
      details:
        'El trasdosado consiste en aplicar una capa de pladur sobre una pared existente, mejorando su aislamiento térmico y acústico, ocultando instalaciones o corrigiendo irregularidades. Puede ser directo (pegado) o autoportante (con estructura metálica).',
      includes: [
        'Trasdosado directo o autoportante según necesidad',
        'Aislamiento térmico (lana mineral o poliestireno)',
        'Placas de pladur estándar o especiales',
        'Tratamiento de juntas',
        'Superficie lista para pintar'
      ],
      useCases: [
        'Mejorar aislamiento de fachadas',
        'Ocultar instalaciones en paredes',
        'Corregir paredes irregulares',
        'Reducir transmisión de ruido entre viviendas'
      ],
      priceFactors:
        'El coste varía según los m² a trasdo sar, el tipo de trasdosado (directo o autoportante), el grosor del aislamiento y el tipo de placa utilizada.',
      image:
        'https://readdy.ai/api/search-image?query=interior%20wall%20insulation%20installation%20with%20drywall%20backing%20system%20thermal%20acoustic%20insulation%20materials%20professional%20construction%20dark%20atmospheric%20lighting%20clean%20workspace&width=800&height=600&seq=service-trasdosado-007&orientation=landscape'
    },
    {
      id: 'techos',
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      description: 'Continuos y registrables que mejoran estética y funcionalidad',
      details:
        'Los falsos techos de pladur ocultan instalaciones (cables, tuberías, conductos), mejoran el aislamiento acústico, permiten integrar iluminación empotrada y dan un acabado limpio y moderno. Pueden ser continuos (fijos) o registrables (desmontables).',
      includes: [
        'Estructura metálica suspendida',
        'Placas de pladur continuas o registrables',
        'Aislamiento acústico opcional',
        'Preparación para focos empotrados',
        'Acabado liso listo para pintar'
      ],
      useCases: [
        'Ocultar instalaciones en techos',
        'Bajar altura de techos muy altos',
        'Integrar iluminación LED empotrada',
        'Mejorar acústica en oficinas y locales'
      ],
      priceFactors:
        'El precio depende de los m² de techo, la altura de la sala, si es continuo o registrable, el nivel de aislamiento y la complejidad de la instalación eléctrica.',
      image:
        'https://readdy.ai/api/search-image?query=modern%20suspended%20ceiling%20installation%20with%20drywall%20panels%20clean%20white%20surface%20integrated%20lighting%20professional%20construction%20dark%20atmospheric%20lighting%20minimalist%20interior&width=800&height=600&seq=service-techos-008&orientation=landscape'
    },
    {
      id: 'acustico',
      icon: 'ri-volume-mute-line',
      title: 'Aislamiento Acústico',
      description: 'Soluciones de insonorización para mayor confort',
      details:
        'El aislamiento acústico con pladur reduce la transmisión de ruido entre estancias, plantas o viviendas. Se consigue mediante sistemas de doble placa, lana de roca de alta densidad y perfiles antivibratorios.',
      includes: [
        'Doble placa de pladur',
        'Lana de roca de alta densidad',
        'Perfiles antivibratorios',
        'Banda elástica perimetral',
        'Sellado acústico de juntas'
      ],
      useCases: [
        'Aislar dormitorios del ruido exterior',
        'Insonorizar salas de música o home cinema',
        'Reducir ruido entre viviendas',
        'Mejorar confort en oficinas'
      ],
      priceFactors:
        'El coste depende del nivel de aislamiento deseado (dB a reducir), los m² a tratar, el número de capas de placa y el tipo de aislante utilizado.',
      image:
        'https://readdy.ai/api/search-image?query=acoustic%20insulation%20installation%20with%20mineral%20wool%20and%20drywall%20soundproofing%20materials%20professional%20construction%20dark%20atmospheric%20lighting%20quality%20craftsmanship%20details&width=800&height=600&seq=service-acustico-009&orientation=landscape'
    },
    {
      id: 'termico',
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento Térmico',
      description: 'Mejora la eficiencia energética de tu espacio',
      details:
        'El aislamiento térmico con pladur reduce pérdidas de calor en invierno y mantiene el frescor en verano, mejorando el confort y reduciendo el consumo energético. Se instala en trasdosados de fachadas, techos y tabiques exteriores.',
      includes: [
        'Aislante térmico (lana mineral, poliestireno o XPS)',
        'Trasdosado con pladur',
        'Barrera de vapor si es necesario',
        'Sellado de juntas',
        'Acabado listo para pintar'
      ],
      useCases: [
        'Mejorar eficiencia energética de fachadas',
        'Aislar techos bajo cubierta',
        'Reducir condensaciones',
        'Bajar consumo de calefacción y aire acondicionado'
      ],
      priceFactors:
        'El precio varía según los m² a aislar, el grosor del aislante, el tipo de material aislante y la complejidad de la instalación.',
      image:
        'https://readdy.ai/api/search-image?query=thermal%20insulation%20installation%20with%20mineral%20wool%20and%20drywall%20energy%20efficiency%20materials%20professional%20construction%20dark%20atmospheric%20lighting%20clean%20workspace%20quality&width=800&height=600&seq=service-termico-010&orientation=landscape'
    },
    {
      id: 'antihumedad',
      icon: 'ri-drop-line',
      title: 'Pladur Antihumedad',
      description: 'Placas especiales para zonas húmedas',
      details:
        'Las placas de pladur antihumedad (WA) están diseñadas para resistir la humedad y el vapor de baños, cocinas, lavaderos y sótanos. Tienen un núcleo tratado que evita la absorción de agua y previene la aparición de moho.',
      includes: [
        'Placas de pladur WA (antihumedad)',
        'Estructura metálica resistente',
        'Tratamiento de juntas con productos hidrófugos',
        'Preparación para alicatar o pintar',
        'Garantía contra humedad'
      ],
      useCases: [
        'Tabiques y trasdosados en baños',
        'Techos de cocinas y lavaderos',
        'Zonas con alta humedad ambiental',
        'Sótanos y garajes'
      ],
      priceFactors:
        'El coste depende de los m² a instalar, el tipo de acabado (alicatado o pintado) y la complejidad de la zona (esquinas, instalaciones).',
      image:
        'https://readdy.ai/api/search-image?query=moisture%20resistant%20drywall%20installation%20in%20bathroom%20interior%20water%20resistant%20green%20board%20professional%20construction%20dark%20atmospheric%20lighting%20clean%20modern%20bathroom&width=800&height=600&seq=service-antihumedad-011&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F3F3F3]">
      <Header />
      <WhatsAppButton />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF007A]/10 via-[#0B0B0F] to-[#0B0B0F]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 break-words">
              <strong>Servicios de pladur</strong>
              <span className="block mt-2 text-[#FF007A]">en Mataró y comarca</span>
            </h1>
            <p className="text-lg text-[#F3F3F3]/90 max-w-3xl mx-auto leading-relaxed break-words">
              Soluciones completas de <strong>instalación de pladur en Mataró</strong> para viviendas, oficinas y locales. Desde tabiques hasta aislamientos, con acabados profesionales. <Link to="/empresa" className="text-[#FF007A] hover:underline">Conoce nuestro equipo</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="scroll-mt-24"
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mb-6">
                      <i className={`${service.icon} text-2xl sm:text-3xl text-[#FF007A]`}></i>
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 break-words">{service.title}</h2>
                    <p className="text-base sm:text-lg text-[#F3F3F3]/70 mb-6 leading-relaxed break-words">{service.details}</p>

                    <div className="mb-6">
                      <h3 className="text-lg sm:text-xl font-semibold mb-3 text-[#FF007A] break-words">Qué incluye</h3>
                      <ul className="space-y-2">
                        {service.includes.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-checkbox-circle-fill text-[#FF007A] text-lg mr-3 mt-1 flex-shrink-0"></i>
                            <span className="text-[#F3F3F3]/70 text-sm break-words">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg sm:text-xl font-semibold mb-3 text-[#FF007A] break-words">Usos habituales</h3>
                      <div className="flex flex-wrap gap-2">
                        {service.useCases.map((useCase, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 bg-[#12121A] rounded-full text-xs text-[#F3F3F3]/80 border border-[#FF007A]/20 break-words"
                          >
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-[#12121A] rounded-lg p-4 mb-6 border border-[#FF007A]/10">
                      <h3 className="text-sm font-semibold mb-2 text-[#FF007A] break-words">
                        Factores que afectan al precio
                      </h3>
                      <p className="text-sm text-[#F3F3F3]/70 break-words">{service.priceFactors}</p>
                    </div>

                    <a
                      href="tel:+34629501941"
                      className="inline-block px-6 py-3 bg-[#FF007A] text-white rounded-lg font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Solicitar Presupuesto
                    </a>
                  </div>

                  <div
                    className={`relative h-64 sm:h-80 lg:h-96 rounded-xl overflow-hidden w-full ${
                      index % 2 === 1 ? 'lg:order-1' : ''
                    }`}
                  >
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 break-words">
              Reformas <span className="text-[#FF007A]">integrales</span>
            </h2>
            <p className="text-base sm:text-lg text-[#F3F3F3]/70 max-w-3xl mx-auto leading-relaxed break-words">
              Además de servicios individuales, realizamos reformas completas con pladur para redistribuir espacios en viviendas, oficinas y locales comerciales en Mataró, el Maresme y Barcelona. ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-[#FF007A] hover:underline">preguntas frecuentes</Link>.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#0B0B0F] rounded-xl p-6 border border-[#FF007A]/10"
            >
              <i className="ri-home-4-line text-3xl text-[#FF007A] mb-4"></i>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 break-words">Viviendas</h3>
              <p className="text-[#F3F3F3]/70 text-sm leading-relaxed break-words">
                Redistribución de espacios, creación de dormitorios, vestidores, baños adicionales y mejora de aislamientos.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#0B0B0F] rounded-xl p-6 border border-[#FF007A]/10"
            >
              <i className="ri-building-2-line text-3xl text-[#FF007A] mb-4"></i>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 break-words">Oficinas</h3>
              <p className="text-[#F3F3F3]/70 text-sm leading-relaxed break-words">
                Creación de despachos, salas de reuniones, zonas de trabajo y mejora acústica para mayor productividad.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#0B0B0F] rounded-xl p-6 border border-[#FF007A]/10"
            >
              <i className="ri-store-2-line text-3xl text-[#FF007A] mb-4"></i>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 break-words">Locales</h3>
              <p className="text-[#F3F3F3]/70 text-sm leading-relaxed break-words">
                Adaptación de espacios comerciales, creación de probadores, almacenes y zonas diferenciadas.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-[#0B0B0F] to-[#12121A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              ¿Necesitas un <span className="text-[#FF007A]">presupuesto?</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros y te preparamos un presupuesto detallado sin compromiso. Visitamos tu proyecto en Mataró y alrededores.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="px-8 py-4 bg-[#FF007A] text-white rounded-lg text-base font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                629 501 941
              </a>
              <Link
                to="/contacto"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                Formulario de contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BackToTopButton />
    </div>
  );
};

export default ServiciosPage;
